import OSS from 'ali-oss';
import dayjs from 'dayjs';
import { cloneDeep, isNil } from 'lodash-es';
import CryptoJS from 'crypto-js';
import { history, Link } from '@umijs/max';
import DOMPurify from 'dompurify';

import { fetchAliOSS, AliOSSProps, OSS_ACCESS_URL } from '@/services/ali-oss';
import { outLogin } from '@/services/logic/login';
import { LocalStoreModule } from '@/constants/common';
import { clearModuleLocalStore } from '@/hooks/useLocalStore';
import { Dropdown, MenuProps, message, Space } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { clearToken } from './common/authToken';

// import { stringify } from 'querystring';

export const cnNameReg = /^[\u4E00-\u9FA5a-zA-Z0-9-]+$/u;
export const enNameReg = /^[a-zA-Z]+$/;
export const passwordReg = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])\S+$/;
export const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const noCnReg = /^[^\u4E00-\u9FA5]+$/;
const salt = 'disputes-order-salt-v01';

// 获取OSS上的地址
export const getOSSUrl = (path = '') => `${OSS_ACCESS_URL}/${path}`;

// 获取oss的sts临时令牌
export const getOssClient = async () => {
  const ossClient = sessionStorage.getItem('ossClient');
  const getOss = (token: AliOSSProps) =>
    new OSS({
      // yourRegion填写Bucket所在地域。以华东1（杭州）为例，yourRegion填写为oss-cn-hangzhou。
      region: token.Region,
      accessKeyId: token.AccessKeyId,
      accessKeySecret: token.AccessKeySecret,
      stsToken: token.SecurityToken,
      // 填写Bucket名称，例如examplebucket。
      bucket: token.Bucket,
      secure: true,
      refreshSTSTokenInterval: 15 * 60 * 1000,
      // 刷新临时访问凭证。
      refreshSTSToken: async () => {
        const refreshToken = await fetchAliOSS();
        sessionStorage.setItem('ossClient', JSON.stringify(token));
        return {
          accessKeyId: refreshToken.AccessKeyId,
          accessKeySecret: refreshToken.AccessKeySecret,
          stsToken: refreshToken.SecurityToken,
        };
      },
    });
  if (ossClient) {
    const token = JSON.parse(ossClient);
    if (token?.Expiration && dayjs().isBefore(token?.Expiration)) {
      return getOss(token);
    }
  }
  const token = await fetchAliOSS();
  sessionStorage.setItem('ossClient', JSON.stringify(token));
  return getOss(token);
};

/**
 * 将当前的 url 保存,返回登陆页面
 */
export const toLogin = async () => {
  clearToken();
  // const { search, pathname } = window.location;
  const urlParams = new URL(window.location.href).searchParams;
  /** 此方法会跳转到 redirect 参数所在的位置 */
  const redirect = urlParams.get('redirect');
  // Note: There may be security issues, please note
  if (window.location.pathname !== '/user/login' && !redirect) {
    window.location.replace('/user/login'); // NOTE: 因为接入了 阿里 ARMS，则每次退出登录后，需要刷新页面，否则无法正常更新 监控中的 username
  }
};

/**
 * 退出登录，并且将当前的 url 保存,返回登陆页面
 */
export const loginOut = async () => {
  await outLogin();
  toLogin();
};

// 获取用户token
export const getToken = () => {
  const tokenStr = getAccessToken() as string;
  if (tokenStr) {
    const token = JSON.parse(tokenStr);
    return token;
  }
  return null;
};

// 格式化为filter
export const formatTableGetParams = (params: Record<string, any>) => {
  const filter = {} as any;
  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      switch (key) {
        case 'current':
          filter.page = params[key];
          break;
        case 'pageSize':
          filter.per_page = params[key];
          break;
        default:
          filter[`filter[${key}]`] = params[key];
          break;
      }
    }
  }
  return filter;
};
export const formatTablePostParams_new = (params: Record<string, any>) => {
  const newParams = {} as any;
  const filter = {} as any;
  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      switch (key) {
        case 'current':
          newParams.page = params[key];
          break;
        case 'pageSize':
          newParams.per_page = params[key];
          break;
        default:
          filter[key] = params[key];
          break;
      }
    }
  }
  newParams.filter = filter;
  return newParams;
};

// 格式化page和pageSize !NOTE by OA 系统
export const formatTablePostParams = (params: Record<string, any>) => {
  const filter = {} as any;
  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      switch (key) {
        case 'current':
          filter.page = params[key];
          break;
        case 'pageSize':
          filter.per_page = params[key];
          break;
        default:
          filter[key] = params[key];
          break;
      }
    }
  }
  return filter;
};

// 格式化为filter !NOTE by 智能回邮
export const formatTableParams = (params: Record<string, any>) => {
  const filter = {} as any;
  for (const key in params) {
    switch (key) {
      case 'current':
        filter.page = params[key];
        break;
      case 'pageSize':
        filter.per_page = params[key];
        break;
      default:
        filter[`filter[${key}]`] = params[key];
        break;
    }
  }
  return filter;
};

// 格式化为展示需要的数据格式
export const formatTableResponse = (data: Record<string, any>) => {
  if (!Array.isArray(data?.lists)) {
    return data;
  }

  const { currentPage, lists, pageSize, total } = data;
  const responseFormat = {
    current: currentPage,
    data: lists,
    pageSize: pageSize,
    total: total,
    success: true,
  };

  return responseFormat;
};

// 清除空字符串、null和undefined
export const clearFalseValue = (compactParam: any) => {
  const _compactParam = cloneDeep(compactParam);
  for (const key in _compactParam) {
    if (
      isNil(_compactParam[key]) ||
      _compactParam[key] === '' ||
      (Array.isArray(_compactParam[key]) && _compactParam[key].length === 0)
    ) {
      delete _compactParam[key];
    }
  }
  return _compactParam;
};

export const stopPageTurning = (e: any) => {
  if (e.keyCode === 75) {
    e.stopPropagation();
  }
  if (e.keyCode === 74) {
    e.stopPropagation();
  }
};

export const item2Array = (data: Record<string, any>, keys: string[]) => {
  const param = cloneDeep(data);
  keys.forEach((item: string) => {
    if (param && param?.[item] && typeof param?.[item] === 'string') {
      param[item] = param?.[item].split(',');
    }
  });
  return param;
};

export const formatJson = (data: any[], keyMap: Record<string, string>) => {
  if (Array.isArray(data) && data.length && keyMap && typeof keyMap === 'object') {
    const keys = Object.keys(keyMap);
    return data.map((item: Record<string, any>) => {
      const coloneItem = cloneDeep(item);
      keys.forEach((keyItem: string) => {
        if (coloneItem[keyItem]) {
          coloneItem[keyMap[keyItem]] = coloneItem[keyItem];
          // delete coloneItem[keyItem];
        }
      });
      keys.forEach((keyItem: string) => delete coloneItem[keyItem]);
      return coloneItem;
    });
  }
  return [];
};

// 禁止input输入
export const handleKeyPress = (reg: RegExp, event: { key: any; preventDefault: () => void }) => {
  const inputValue = event.key;
  const pattern = reg; // 匹配中文、英文、数字的正则表达式
  if (!pattern.test(inputValue)) {
    event.preventDefault(); // 阻止输入
  }
};

// 使用Key翻译config
export const translateConfig = (key: any, config: any[]) => {
  const translateConfigItem = config.find((item) => item.key === key);
  return translateConfigItem.value;
};
// 路由白名单检测
export const isRouterWhitePass = (url?: string): boolean => {
  let passRouters: any[] = [];
  const { location } = history;
  const mergeUrl = url ? url : location.pathname;
  // TODO:临时方案
  if (process.env) {
    // 公共业务组件路由为白名单
    if (location.pathname.split('/').includes('Dev')) return true;
  }
  // if (location){}
  return passRouters.some((item) => {
    return item.path === mergeUrl;
  });
};

// 净化金额
export const pureAmount = (amount: string) => {
  let pure = false;
  return amount.replace(/[^0-9.]/g, '').replace(/(\.)/g, () => (pure ? '' : (pure = true) && '.'));
};

export const isPro = () => window.ENVIRONMENT_TYPE === 'production';
export const isStaging = () => window.ENVIRONMENT_TYPE === 'staging';

export const urlMap = {
  // 生产环境
  production: 'https://chat.knowmails.com',
  // 预生产环境
  staging: 'https://chat-staging.knowmails.com',
  // 迭代开发环境
  develop: 'https://chat-iteration-develop.knowmails.com',
  // 迭代测试环境
  test: 'https://chat-iteration-test.knowmails.com',
  //特殊测试环境
  'hotfix-test': 'https://chat-hotfix-test.knowmails.com',
  // 模块测试环境
  'module-test': 'https://chat-module-test.knowmails.com',
  // 模块开发环境
  'module-develop': 'https://chat-module-develop.knowmails.com',
} as Record<string, string>;

export const ossUrlMap = {
  // 迭代开发环境
  develop: 'https://chat-iteration-develop.knowmails.com',
  // 迭代测试环境
  test: 'https://chat-iteration-test.knowmails.com',
  //特殊测试环境
  'hotfix-test': 'https://chat-hotfix-test.knowmails.com',
  // 模块测试环境
  'module-test': 'https://chat-module-test.knowmails.com',
  // 模块开发环境
  'module-develop': 'https://chat-module-develop.knowmails.com',
  // 生产环境
  production: 'https://chat-production.knowmails.com',
} as Record<string, string>;

// 加密，生成 IM 端链接所需的 token
export const encryptionToken = (
  host_domain: string,
  order_id: number | string,
  transaction_id: string,
  number: string,
) => {
  // 将各个字段拼接成一个字符串
  const data = `${host_domain}|${order_id}|${transaction_id}|${number}`;
  // 使用 UTF-8 编码将字符串转换为 WordArray
  const utf8Data = CryptoJS.enc.Utf8.parse(data);
  // 使用 Base64 编码将 WordArray 转换为 Base64 字符串
  const base64Data = CryptoJS.enc.Base64.stringify(utf8Data);
  // 创建 DES 加密器
  const method = CryptoJS.algo.DES.createEncryptor(CryptoJS.enc.Utf8.parse(salt), {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  });
  // 对 Base64 字符串进行加密
  const encrypted = method.finalize(CryptoJS.enc.Base64.parse(base64Data));
  // 将加密后的结果转换为 Base64 字符串并返回
  return encodeURIComponent(encrypted.toString(CryptoJS.enc.Base64));
};

export const formatFileSize = (bytes: number): string => {
  const units = ['B', 'KB', 'MB', 'GB'];
  const thresholds = [1024, 1048576, 1073741824];

  if (bytes === 0) return `0 ${units[1]}`;

  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (bytes >= thresholds[i]) {
      return `${(bytes / thresholds[i]).toFixed(2)} ${units[i + 1]}`;
    }
  }

  return `${bytes} ${units[0]}`;
};

export const validateEmail = (email: string) => {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,}$/;
  return re.test(String(email).toLowerCase());
};
// 邮箱校验简易版
export const validateEmailSimple = (email: string) => {
  const re = /^.+@.+$/;
  return re.test(String(email).toLowerCase());
};
// 获取当前环境的 OSS 下载时访问地址 处理人
export const OSS_ACCESS_URL_MAPS: any = {
  // 生产环境
  production: 'https://qdesk-im-apps.knowmails.com',
  // 预生产环境
  staging: 'https://qdesk-im-stag-apps.knowmails.com',
  // 迭代开发环境
  develop: '',
  // 迭代测试环境
  test: '',
  //特殊测试环境
  'hotfix-test': '',
  // 模块测试环境
  'module-test': '',
  // 模块开发环境
  'module-develop': '',
};

// 获取当前环境的 OSS 下载时访问地址 顾客
export const OSS_ACCESS_URL_MAPS_TO_CUSTOMER: any = {
  // 生产环境
  production: 'https://qdesk-im-apps.oss-us-west-1.aliyuncs.com',
  // production: 'https://qdesk-im-apps.oss-accelerate.aliyuncs.com',
  // 预生产环境
  staging: 'https://qdesk-im-stag-apps.knowmails.com',
  // 迭代开发环境
  develop: '',
  // 迭代测试环境
  test: '',
  //特殊测试环境
  'hotfix-test': '',
  // 模块测试环境
  'module-test': '',
  // 模块开发环境
  'module-develop': '',
};

const defaultENV = 'develop';

// 获取当前环境的 OSS 下载时访问地址
export const getOSSAccessUrl = (env = defaultENV) => {
  return OSS_ACCESS_URL_MAPS[window?.ENVIRONMENT_TYPE || env];
};

const defaultOSSUrl = 'https://qdesk-im-apps.oss-accelerate.aliyuncs.com';

// 将顾客连接转为处理人连接
export const customerUrlToServiceUrl = (url: string, env = defaultENV) => {
  const servicePath = OSS_ACCESS_URL_MAPS[window?.ENVIRONMENT_TYPE || env];
  const customerPath = OSS_ACCESS_URL_MAPS_TO_CUSTOMER[window?.ENVIRONMENT_TYPE || env];
  return url.replaceAll(customerPath, servicePath).replaceAll(defaultOSSUrl, servicePath);
};

// 将处理人连接转为顾客连接
export const serviceUrlToCustomerUrl = (url: string, env = defaultENV) => {
  const servicePath = OSS_ACCESS_URL_MAPS[window?.ENVIRONMENT_TYPE || env];
  const customerPath = OSS_ACCESS_URL_MAPS_TO_CUSTOMER[window?.ENVIRONMENT_TYPE || env];
  return url.replaceAll(servicePath, customerPath).replaceAll(defaultOSSUrl, customerPath);
};

// 全局缓存：往 window 中注入缓存
export const globalCache = {
  prefix: 'qdesk_global_cache_',
  get(prop: any) {
    const key = `${this.prefix}_${prop}`
    if (key in window) {
      return window[key as any] as any;
    }
    return null;
  },
  set(prop: any, value: any) {
    const key = `${this.prefix}_${prop}`
    window[key as any] = value;
    return true;
  },
};

export const isValidOSSFileName = (fileName: string) => {
  // 基础正则：允许字母、数字、-_.!~*'()和空格
  const basePattern = /^[a-zA-Z0-9\-_.!~*'() ]+$/;

  // 增强正则：额外允许中文等Unicode字符
  // const unicodePattern = /^[\u4E00-\u9FA5a-zA-Z0-9\-_.!~*'() ]+$/;

  // 选择其中一个正则（根据需求）
  return basePattern.test(fileName);

  // 如果需要允许中文，使用：
  // return unicodePattern.test(fileName);
}

// 字数统计方法
export const getWordCount = (content: string): number => {
  const textNodes: HTMLElement[] = [];
  const walker = (node: HTMLElement) => {
    if (['script', 'style', 'noscript', 'video', 'img', 'a'].includes(node.tagName?.toLowerCase())) return;
    if (node.classList?.contains('attachment-container')) {
      return;
    }
    if (node.childNodes.length === 0 && node.textContent?.trim()) {
      textNodes.push(node);
    } else {
      node.childNodes.forEach((child) => walker(child as HTMLElement));
    }
  };

  const root = document.createElement('div');
  root.innerHTML = content;
  walker(root as HTMLElement);
  const originalTexts = textNodes.map((node) => node.textContent?.trim());
  return originalTexts.join('').length;
};

// 清洗html文本，防止注入攻击
export const clearXssHtmlString = (htmlString: string, options?: DOMPurify.Config) => {
  return DOMPurify.sanitize(htmlString, options);
}
export const PAYCLOAK_URL_PATH: any = {
  // 生产环境
  production: 'http://pp.disputebill.com',
  // 预生产环境
  staging: 'http://staging.disputebill.com',
  // 迭代开发环境
  develop: 'http://develop.disputebill.com',
  // 迭代测试环境
  test: 'http://test.disputebill.com',
  //特殊测试环境
  'hotfix-test': 'http://test.disputebill.com',
  // 模块测试环境
  'module-test': 'http://test.disputebill.com',
  // 模块开发环境
  'module-develop': 'http://develop.disputebill.com',
};
// OA项目环境
export const OA_URL_PATH: any = {
  // 生产环境
  production: 'https://www.teamoa.cn',
  // 预生产环境
  staging: 'https://staging.teamoa.cn',
  // 迭代开发环境
  // develop: 'https://develop.teamoa.cn',
  // develop: 'http://localhost:8000',
  develop: 'https://test.teamoa.cn',
  // 迭代测试环境
  test: 'https://test.teamoa.cn',
  //特殊测试环境
  'hotfix-test': 'https://test.teamoa.cn',
  // 模块测试环境
  'module-test': 'https://test.teamoa.cn',
  // 模块开发环境
  'module-develop': 'https://develop.teamoa.cn',
};

// 获取当前环境的 售后系统 下载时访问地址
export const getPaycloakUrlPath = (env = defaultENV) => {
  return PAYCLOAK_URL_PATH[window?.ENVIRONMENT_TYPE || env];
};
// 获取当前环境的 OA 项目访问地址
export const getOaUrlPath = (env = defaultENV) => {
  return OA_URL_PATH[window?.ENVIRONMENT_TYPE || env];
};
// 将dom片段中的 https wwww http 文本转化为超链接
export const convertDomToLink = (domInnerText: string) => {
  if (domInnerText) {
    let processedContent = domInnerText;
    const aTagPlaceholder = '___A_TAG_PLACEHOLDER___';
    const imgTagPlaceholder = '___IMG_TAG_PLACEHOLDER___';
    const videoTagPlaceholder = '___VIDEO_TAG_PLACEHOLDER___';
    const existingATags: string[] = [];
    const existingImgTags: string[] = [];
    const existingVideoTags: string[] = [];

    // 清理URL末尾标点符号的通用函数
    const cleanUrl = (url: string) => url.replace(/[。，、；：""''（）《》【】！？.,:;!?()[\]{}]+$/, '');

    // 生成链接的通用函数
    const createLink = (url: string, href?: string) =>
      `<a href="${href || url}" target="_blank" rel="noopener noreferrer">${url}</a>`;

    // 保护现有img标签
    processedContent = processedContent.replace(/<img\s[^>]*>/gi, (match: string) => {
      const index = existingImgTags.length;
      existingImgTags.push(match);
      return `${imgTagPlaceholder}${index}${imgTagPlaceholder}`;
    });

    // 保护现有video标签
    processedContent = processedContent.replace(/<video\s[^>]*>.*?<\/video>/gi, (match: string) => {
      const index = existingVideoTags.length;
      existingVideoTags.push(match);
      return `${videoTagPlaceholder}${index}${videoTagPlaceholder}`;
    });

    // 保护现有a标签
    processedContent = processedContent.replace(/<a\s[^>]*>.*?<\/a>/gi, (match: string) => {
      const index = existingATags.length;
      existingATags.push(match);
      return `${aTagPlaceholder}${index}${aTagPlaceholder}`;
    });

    // 按引号分割，只处理非引号内容（避免处理标签属性）
    const parts = processedContent.split(/("[^"]*"|'[^']*')/);

    for (let i = 0; i < parts.length; i += 2) {
      let part = parts[i];

      // 处理http(s)链接
      part = part.replace(/(^|[^a-zA-Z0-9])(https?:\/\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+)/gi,
        (match: string, prefix: string, url: string) => {
          const cleanedUrl = cleanUrl(url);
          return prefix + createLink(cleanedUrl);
        }
      );

      // 处理www链接（避免处理已在a标签内的）
      part = part.replace(/(^|[^a-zA-Z0-9])(www\.[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+)(?![^<]*<\/a>)/gi,
        (match: string, prefix: string, url: string) => {
          const beforeMatch = part.substring(0, part.indexOf(match));
          const inATag = beforeMatch.lastIndexOf('<a') > beforeMatch.lastIndexOf('</a>');

          if (inATag) return match;

          const cleanedUrl = cleanUrl(url);
          return prefix + createLink(cleanedUrl, `http://${cleanedUrl}`);
        }
      );

      parts[i] = part;
    }

    // 恢复img标签
    processedContent = parts.join('').replace(
      new RegExp(`${imgTagPlaceholder}(\\d+)${imgTagPlaceholder}`, 'g'),
      (match: string, index: string) => existingImgTags[parseInt(index)] || match
    );

    // 恢复video标签
    processedContent = processedContent.replace(
      new RegExp(`${videoTagPlaceholder}(\\d+)${videoTagPlaceholder}`, 'g'),
      (match: string, index: string) => existingVideoTags[parseInt(index)] || match
    );

    // 恢复a标签
    processedContent = processedContent.replace(
      new RegExp(`${aTagPlaceholder}(\\d+)${aTagPlaceholder}`, 'g'),
      (match: string, index: string) => existingATags[parseInt(index)] || match
    );

    return processedContent;
  }
}

// 文件转base64
export const fileToBase64 = (file: File, callback: (base64: string) => void) => {
  const reader = new FileReader();
  reader.onload = function (e: ProgressEvent<FileReader>) {
    if (e.target?.result) {
      callback(e.target.result as string); // e.target.result 就是 base64 字符串
    }
  };
  reader.readAsDataURL(file); // 将文件读取为 base64 Data URL
};

// 密码转码
export const encryptionPassword = (password: string) => {
  let ivString = '1234567890123456';
  // for (let i = 0; i < 16; i++) {
  //   ivString += Math.floor(Math.random() * 10).toString(); // 生成 0 到 9 之间的随机数
  // }
  const iv = CryptoJS.enc.Utf8.parse(ivString);
  const key = CryptoJS.enc.Utf8.parse('ADMIN_OAADMIN_OA');
  const encryptedData = CryptoJS.AES.encrypt(password, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  return encryptedData.toString();
  // return btoa(`${ivString}${encryptedData.toString()}`);
};


// 合并订单数据方法
export const mergeOrderDataFn = (extraField: any) => {
  try {
    // 如果有 extraField 且 extraField不为空对象
    if (extraField && Object.keys(extraField).length > 0) {
      // 对 extraField 中的 shopify_items 进行遍历，
      // variant_title 转成对象，键值对只取值，若大于一个用 "_" 分割
      extraField?.shopify_items?.forEach((item: any) => {
        item.variant_title = JSON.parse(item.variant_title);
        item.variant_title = Object.values(item.variant_title).join('_');
      });

      return extraField;
    }
  } catch (error) {
    return extraField;
  }
};

/**
 * 清除所有以指定前缀开头的 localStorage key
 */
export function clearLocalStorageByPrefix(prefix: string) {
  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    return;
  }
}

/**
 * 清除 QDESK_MODULE_MAPPING 缓存
 */
export function clearLocalStorageByModuleMapping() {
  // 没有就不做
  if (!localStorage?.getItem) {
    return;
  }
  // 查看localStorage中是否存在 清除旧缓存的标记
  const clearCacheMark = localStorage.getItem('CLEAR_CACHE_MARK');
  if (clearCacheMark) {
    return;
  }
  //
  const userName = localStorage.getItem('QDESK_USER_NAME')
  // 需要被清除的本地存储键
  const keysStrToRemove = `${userName}_/email-ticket/detail/`
  // 遍历本地storage，如果key包含keysStrToRemove中的str，则删除
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes(keysStrToRemove)) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.error('clearLocalStorageByModuleMapping error', error);
  }
  // 刷新后清除本地localStore模块缓存 LocalStoreModule所有枚举项
  Object.values(LocalStoreModule).forEach(module => {
    clearModuleLocalStore(module);
  });
  localStorage.setItem('CLEAR_CACHE_MARK', 'true');
}

export const handleCopy = (text: string) => {
  navigator.clipboard.writeText(text).then(() => {
    message.success('复制成功！');
  }).catch(() => {
    message.error('复制失败！');
  });
};
